import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import MatrixRain from "../components/MatrixRain";

export default function Login({ onSuccess, login }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState("username"); // username | password | loading
  const userRef = useRef(null);
  const passRef = useRef(null);
  const cardRef = useRef(null);
  const linesRef = useRef([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase === "username") {
      if (!username.trim()) return;
      setPhase("password");
      return;
    }
    setPhase("loading");
    setError(null);
    const result = await login(username.trim(), password.trim());
    if (result.ok) {
      setTimeout(() => onSuccess(), 300); // small delay for effect
    } else {
      setError(result.message || "Login failed");
      setPhase("password");
    }
  };

  useEffect(() => {
    if (phase === "username" && userRef.current) {
      userRef.current.focus();
    }
    if (phase === "password" && passRef.current) {
      passRef.current.focus();
    }
  }, [phase]);

  // Animate login card and terminal lines on mount
  useEffect(() => {
    if (cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 30, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "power2.out" }
      );
    }
    const lines = linesRef.current.filter(Boolean);
    if (lines.length) {
      gsap.fromTo(
        lines,
        { opacity: 0, x: -10 },
        { opacity: 1, x: 0, duration: 0.3, stagger: 0.1, delay: 0.3, ease: "power2.out" }
      );
    }
  }, []);

  return (
    <div className="login-screen">
      <MatrixRain opacity={0.25} speed={0.5} />
      <div className="login-glow" />
      <form className="login-card" onSubmit={handleSubmit} ref={cardRef}>
        <div className="login-terminal">
          <div className="terminal-line" ref={el => linesRef.current[0] = el}>
            <span className="prompt-symbol">$</span>
            <span> {t('login.accessNode')}</span>
          </div>
          <div className="terminal-line" ref={el => linesRef.current[1] = el}>
            <span className="prompt-symbol">$</span>
            <span> {t('login.title')}</span>
          </div>
          <div className="terminal-line" ref={el => linesRef.current[2] = el}>
            <span className="prompt-symbol">$</span>
            <span> {t('login.enterCredentials')}</span>
          </div>
          <div className="terminal-field flat tight">
            <span className="prompt-symbol">$</span>
            <span>{t('login.username')}:</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login.usernamePlaceholder')}
              autoComplete="username"
              className="terminal-input"
              ref={userRef}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
          </div>
          {phase !== "username" && (
            <div className="terminal-field flat tight">
              <span className="prompt-symbol">$</span>
              <span>{t('login.password')}:</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                autoComplete="current-password"
                className="terminal-input"
                ref={passRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>
          )}
          {phase === "loading" && (
            <div className="terminal-line loading-line">
              <span className="prompt-symbol">$</span>
              <span> {t('login.loading')}</span>
              <span className="loading-dots" aria-hidden="true" />
            </div>
          )}
          {error && <div className="error-text">{error}</div>}
          <button type="submit" className="cta">
            {(() => {
              if (phase === "loading") return t('login.connecting');
              if (phase === "username") return t('login.next');
              return t('login.enter');
            })()}
          </button>
          <div className="hint">
            {t('login.hint')}
          </div>
        </div>
      </form>
    </div>
  );
}
